import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import { Pill, Spinner, Field, TextInput, TextArea, SelectInput } from '../../components/ui';
import { OperationEditor } from './OperationEditor';

// 单件返工面板（阶段 2）：共用工序 + 粉末耗用 + 本次返工检验（模板完整快照继承 + 放行/失效）。
// 设计文档 docs/aftersales-rework-batch-redesign-2026-10-16.md v3 §7 / §7.1 / §8。
// 粉末耗用 UI 仅开放「即时扣库」；工单领料核销（work_order_link）在接口层可用，待领料上下文接入后再开放。

type PowderUsage = {
  id: string;
  item_name?: string | null;
  powder_name?: string | null;
  actual_quantity?: number;
  actual_unit?: string | null;
  issued_quantity?: number | null;
  stock_movement_id?: string | null;
  adjust_of_id?: string | null;
  adjustment_reason?: string | null;
};
type Operation = {
  id: string;
  operation_code?: string | null;
  operation_name?: string | null;
  planned_hours?: number | null;
  actual_hours?: number | null;
  planned_clad_hours?: number | null;
  status?: string | null;
  operator_name?: string | null;
  powder_usages?: PowderUsage[];
};
type Inspection = {
  id: string;
  number: string;
  result: string;
  release_state: string;
  inspection_round?: number | null;
  inspection_date?: string | null;
  inspector_name?: string | null;
  invalidated_reason?: string | null;
  /** 已录入的阶段数（后端 listItemInspections 透出）；用于「已录值/待录值」展示。 */
  stage_count?: number | null;
  /** 真实有值的读数项数（后端 listItemInspections 透出）。
   *  注意与 stage_count 的区别：建单会把原单阶段复制过来当初值，stage_count>0 不代表录过值。
   *  2026-10-26 生产反馈：误点建立后显示「已录值」导致删不掉，故改用这两个字段判断。 */
  reading_value_count?: number | null;
  has_readings?: boolean | null;
  /** 本单实际阶段与读数（后端 listItemInspections 透出）。
   *  录值表单以它为准，体现「调出原质检单改数据」而非填模板全量项。 */
  stages?: InspectionStage[] | null;
  /** 本次检验冻结的模板快照（后端 listItemInspections 透出）；无原检验模板时用于录值区检查项。 */
  snapshot_items_json?: string | null;
};
type SnapshotItem = {
  param_key: string;
  param_label_zh?: string | null;
  unit?: string | null;
  required?: number | boolean | null;
  stage_key?: string | null;
  stage_name?: string | null;
  /** 模板声明的测量点数（qc_checkpoint_parameters.points）：平面度等要测 LECTURA 1/2/3 多点。
   *  2026-10-26 生产反馈：此前界面只渲染一个输入框，导致多点项只能录一个值。 */
  points?: number | null;
  /** 本单该参数当前的读数（键为 `参数` 或 `参数_1..N`）。
   *  录值时用它预填 —— 业务口径是「调出原质检单改数据」，不是从空白重填。 */
  current_values?: Record<string, string> | null;
};

/** 本单实际阶段（后端 listItemInspections 透出）。录值表单以此为准，
 *  不再用模板全量项（否则会逼工程师填本次没做的工序）。 */
type InspectionStage = {
  stage_key: string;
  stage_name?: string | null;
  stage_order?: number | null;
  result?: string | null;
  notes?: string | null;
  readings?: Record<string, unknown> | null;
};
/** 原质检单的真实阶段与读数（后端 inspectionContext 透出，不是模板定义）。 */
type SourceStage = {
  id?: string;
  stage_key: string;
  stage_name?: string | null;
  stage_order?: number | null;
  readings?: Record<string, string | number | null> | null;
  notes?: string | null;
};
type Context = {
  template_id?: string | null;
  /** 原检验无可继承模板（或快照不可用且无原模板）时，后端要求质检确认本次模板。 */
  template_confirmation_required?: boolean;
  snapshot_available: boolean;
  snapshot_items: SnapshotItem[];
  original_inspection?: { number?: string; result?: string; qc_template_id?: string | null } | null;
  /** 原单实际阶段（含读数）：售后返工「调出原质检单 → 改数据 → 存成新单」的初值。 */
  source_stages?: SourceStage[];
  source_inspection_stage_count?: number;
  /** 该检验所用模板的工段与参数定义（后端 inspectionContext / 投影透出）。
   *  供「现场按需追加检测项」使用：返修临时增加抛光工序时，可从中选抛光工段追加。 */
  qc_template?: {
    code?: string | null;
    name_es?: string | null;
    name_zh?: string | null;
    stages?: Array<{
      stage_key: string;
      stage_name?: string | null;
      stage_name_es?: string | null;
      parameters?: unknown[] | null;
    }>;
  } | null;
  /** 全系统合法工段与参数（后端 inspectionContext 透出）。
   *  用于「现场自定义追加检测项」：本件模板可能没有现场需要的工段
   *  （例：熔覆修复模板没有抛光），返修临时加工序后仍需补录对应检测。 */
  appendable_stages?: Array<{
    stage_key: string;
    stage_name?: string | null;
    parameters?: string[] | null;
    /** 每参数应测点数（后端下发；厚度/平面度/硬度通常 3 点，对应 LECTURA 1/2/3）。 */
    param_points?: Record<string, number> | null;
  }> | null;
};
type QcTemplate = { id: string; code: string; name_zh: string; active?: number | null };
type ItemRow = { id: string; code?: string; name_zh?: string };
type PowderCandidate = {
  id: string;
  work_order_number?: string | null;
  item_name_zh?: string | null;
  item_code?: string | null;
  issued_base?: number | null;
  remaining_base?: number | null;
};

const RELEASE: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'neutral' | 'bad' }> = {
  draft: { label: '待放行', tone: 'warn' },
  released: { label: '已放行', tone: 'ok' },
  invalidated: { label: '已失效', tone: 'bad' },
};

function parseSnapshotItems(json?: string | null): SnapshotItem[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function ItemReworkPanel({ itemId, itemLabel, canWrite, onChanged }: { itemId: string; itemLabel: string; canWrite: boolean; onChanged: () => void }) {
  const base = `/api/rework-batches/items/${encodeURIComponent(itemId)}`;
  const [ops, setOps] = useState<Operation[]>([]);
  const [insps, setInsps] = useState<Inspection[]>([]);
  const [ctx, setCtx] = useState<Context | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const [powderFor, setPowderFor] = useState<string | null>(null);
  const [powder, setPowder] = useState({ item_id: '', item_label: '', actual_quantity: '', actual_unit: 'kg', issue_mode: 'immediate' as 'immediate' | 'work_order_link', snapshot_id: '' });
  const [powderCandidates, setPowderCandidates] = useState<PowderCandidate[]>([]);
  const [adjustFor, setAdjustFor] = useState<string | null>(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  // 已安排工序编辑：仅改计划工时（PATCH /api/rework-batches/operations/:id），删除走 DELETE。
  const [editOpId, setEditOpId] = useState<string | null>(null);
  const [editOpHours, setEditOpHours] = useState('');
  const [invReason, setInvReason] = useState<Record<string, string>>({});
  const [readingsFor, setReadingsFor] = useState<string | null>(null);
  const [readings, setReadings] = useState<Record<string, string>>({});
  const [readingsItems, setReadingsItems] = useState<SnapshotItem[]>([]);
  const [readingsResult, setReadingsResult] = useState<'' | 'accepted' | 'rejected' | 'conditional'>('');
  /** 现场按需追加检测项：从模板选一个本单尚无的工段，加入录值表单。 */
  const [addStageKey, setAddStageKey] = useState('');
  // 2026-10-23：售后返工质检改为「调出原质检单 → 工程师改数据 → 生成新的返工质检单」。
  // sourceForm 存原单各阶段的读数编辑值（stage_key → {param: value}），note 是本次修改说明。
  const [srcOpen, setSrcOpen] = useState(false);
  const [srcEdit, setSrcEdit] = useState<Record<string, Record<string, string>>>({});
  const [srcNote, setSrcNote] = useState('');
  const [srcDate, setSrcDate] = useState('');
  // 「原检验无模板」分支：质检确认本次模板 + 原因（POST body {template_id, confirmed_reason}）。
  const [templates, setTemplates] = useState<QcTemplate[]>([]);
  const [tplChoice, setTplChoice] = useState({ template_id: '', confirmed_reason: '' });

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    Promise.all([
      api.list<Operation>(`${base}/operations`),
      api.list<Inspection>(`${base}/inspections`),
      api.detail<Context>(`${base}/inspection-context`),
    ])
      .then(([o, i, c]) => { setOps(o); setInsps(i); setCtx(c); })
      .catch((e: any) => setErr(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [base]);
  useEffect(load, [load]);

  // 质检模板候选（仅「原检验无模板」分支使用）：active 优先，其余按 code 排序。
  useEffect(() => {
    let alive = true;
    api.list<QcTemplate>('/api/qc-templates', { limit: 100 })
      .then((list) => {
        if (!alive) return;
        const sorted = [...list].sort((a, b) =>
          (Number(b.active ?? 1) - Number(a.active ?? 1)) || String(a.code).localeCompare(String(b.code)));
        setTemplates(sorted);
      })
      .catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
  }, []);

  // 原检验无可继承模板（或后端显式要求确认）时必须先确认模板，才能建立本次返工检验。
  const templateConfirmationRequired = ctx?.template_confirmation_required === true
    || (!!ctx && !ctx.snapshot_available && !ctx.template_id);

  /** 录值区的检查项。
   *
   * 2026-10-26 业务口径（用户明确）：
   *   「返工工序是为了记录工时和耗料（激光熔覆），与质检无关。
   *    质检之前已有质检单，返工后只需要调用原质检单修改质检数据并生成新的质检单。」
   *
   * 因此录值表单**不能**用模板全量检查项 —— 那会逼工程师填写熔覆/粗磨等
   * 本次返工根本没做的工序（生产实况：一件只做精磨，表单却要求填 10 项）。
   * 正确做法：以**这张单实际有的阶段**为准（建单时已从原质检单复制），
   * 工程师在原有读数上修改即可。只有该单完全没有阶段时，才回退到模板快照。
   */
  /** 录值输入的键：`工段::参数` 或 `工段::参数_序号`。
   *  必须带工段，否则 `thickness` 这类同名参数会在多个工段间互相覆盖
   *  （模板里 dimension/rough_grind_A/fine_grind_bushing 都有 thickness）。 */
  const readingKey = (stageKey: string | null | undefined, paramKey: string) => `${stageKey || 'default'}::${paramKey}`;

  const snapshotItemsFor = (ins: Inspection): SnapshotItem[] => {
    // ① 本单实际阶段 → 转成录值项（参数名 + 当前读数）
    const stages = Array.isArray(ins.stages) ? ins.stages : [];
    if (stages.length) {
      const out: SnapshotItem[] = [];
      for (const st of stages) {
        const readings = st.readings && typeof st.readings === 'object' ? st.readings : {};
        // 同一阶段下把 `参数_1..N` 归并成一项，并记录点数，供多列输入框渲染。
        const bases = new Map<string, { pts: number; values: Record<string, string> }>();
        for (const [k, v] of Object.entries(readings)) {
          const m = /^(.*?)_(\d+)$/.exec(k);
          const base = m ? m[1] : k;
          const idx = m ? Number(m[2]) : 1;
          const cur = bases.get(base) || { pts: 1, values: {} };
          cur.pts = Math.max(cur.pts, idx);
          cur.values[k] = v == null ? '' : String(v);
          bases.set(base, cur);
        }
        for (const [base, info] of bases.entries()) {
          out.push({
            param_key: base,
            param_label_zh: base,
            stage_key: st.stage_key,
            stage_name: st.stage_name,
            unit: null,
            required: null,
            points: info.pts,
            current_values: info.values,
          } as SnapshotItem);
        }
      }
      if (out.length) return out;
    }
    // ② 回退：模板快照（该单还没有任何阶段时）
    const fromSnapshot = parseSnapshotItems(ins.snapshot_items_json);
    return fromSnapshot.length ? fromSnapshot : (ctx?.snapshot_items || []);
  };

  const toggleReadings = (ins: Inspection) => {
    if (readingsFor === ins.id) {
      setReadingsFor(null); setReadings({}); setReadingsItems([]); setReadingsResult('');
      setAddStageKey('');
      return;
    }
    setReadingsFor(ins.id);
    setReadingsResult('');
    const items = snapshotItemsFor(ins);
    setReadingsItems(items);
    // 用本单现有读数预填（「调出原质检单改数据」：改的是已有值，不是从空白填）。
    const prefill: Record<string, string> = {};
    for (const it of items) {
      const cur = (it as SnapshotItem & { current_values?: Record<string, string> }).current_values;
      if (!cur) continue;
      for (const [k, v] of Object.entries(cur)) {
        if (v != null && String(v).trim() !== '') prefill[readingKey(it.stage_key, k)] = String(v);
      }
    }
    setReadings(prefill);
    setAddStageKey('');
  };

  /** 现场按需追加检测项（2026-10-26 生产反馈）。
   *  场景：返修时临时增加了「抛光」工序才修得好，但**本件所用模板里根本没有抛光工段**
   *  （例：熔覆修复模板 bushing_cladding 只有 熔覆/粗磨/精磨）。
   *  因此候选来自系统全部合法工段（appendable_stages），而不是仅本模板 ——
   *  保证现场能按需求自行处理，不必回电脑改模板。 */
  const addStageOptions = (() => {
    const list = Array.isArray(ctx?.appendable_stages) ? ctx!.appendable_stages! : [];
    const have = new Set(readingsItems.map((i) => i.stage_key || ''));
    return list
      .filter((st) => st.stage_key && !have.has(st.stage_key))
      .map((st) => ({
        stage_key: st.stage_key,
        stage_name: st.stage_name || st.stage_key,
        params: Array.isArray(st.parameters) ? st.parameters.map((p) => String(p)) : [],
        paramPoints: (st.param_points || {}) as Record<string, number>,
      }))
      .filter((st) => st.params.length > 0);
  })();

  const addStageItems = () => {
    const pick = addStageOptions.find((o) => o.stage_key === addStageKey);
    if (!pick) return;
    const added: SnapshotItem[] = pick.params.map((p) => ({
      param_key: p,
      param_label_zh: p,
      stage_key: pick.stage_key,
      stage_name: pick.stage_name,
      unit: null,
      required: null,
      // 点数取后端下发的 param_points（2026-10-27：抛光厚度/平面度各 3 个读数，
      // 不再固定 1 个，否则现场只能录一格、出单也只有一格）。
      points: Number(pick.paramPoints[p]) > 0 ? Number(pick.paramPoints[p]) : 1,
    }));
    setReadingsItems((prev) => [...prev, ...added]);
    setAddStageKey('');
  };

  const removeReadingItem = (item: SnapshotItem) => {
    // 仅从本次录入表单移除；保存时按提交内容重写阶段（未提交的项即被清掉）。
    setReadingsItems((prev) => prev.filter((x) => !(x.param_key === item.param_key && x.stage_key === item.stage_key)));
    setReadings((s) => {
      const next = { ...s };
      const prefix = readingKey(item.stage_key, item.param_key);
      for (const k of Object.keys(next)) {
        if (k === prefix || k.startsWith(`${prefix}_`)) delete next[k];
      }
      return next;
    });
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true); setErr(''); setMsg('');
    try { await fn(); setMsg(ok); load(); onChanged(); }
    catch (e: any) { setErr(e?.message || '操作失败'); }
    finally { setBusy(false); }
  };

  const resetPowder = () => { setPowderFor(null); setPowder({ item_id: '', item_label: '', actual_quantity: '', actual_unit: 'kg', issue_mode: 'immediate', snapshot_id: '' }); setPowderCandidates([]); };

  const addPowder = (opId: string) => run(
    () => api.post(`/api/rework-batches/operations/${encodeURIComponent(opId)}/powder`, {
      item_id: powder.item_id, actual_quantity: Number(powder.actual_quantity), actual_unit: powder.actual_unit || 'kg',
      powder_name: powder.item_label || undefined,
      issue_mode: powder.issue_mode,
      ...(powder.issue_mode === 'work_order_link' ? { material_snapshot_id: powder.snapshot_id } : {}),
    }),
    powder.issue_mode === 'work_order_link' ? '已按工单领料核销（差额补扣）' : '耗用已登记并扣库',
  ).then(resetPowder);

  const loadPowderCandidates = (itemId: string) => {
    setPowderCandidates([]);
    if (!itemId) return;
    api.list<PowderCandidate>('/api/rework-batches/powder-link-candidates', { item_id: itemId })
      .then(setPowderCandidates)
      .catch(() => setPowderCandidates([]));
  };

  const submitReadings = (insId: string) => {
    const items = readingsItems;
    const byStage = new Map<string, Record<string, string>>();
    for (const it of items) {
      // 多点项（如平面度测 3 点）在界面上的键是 `参数_1..N`，必须逐个收集，
      // 否则只提交第一点、其余点丢失（2026-10-26 生产反馈）。
      // 输入键带工段前缀（`工段::参数`），提交给后端的键则只保留参数名。
      const pts = Math.max(1, Math.min(6, Number(it.points) || 1));
      const suffixes = pts === 1 ? [''] : Array.from({ length: pts }, (_, i) => `_${i + 1}`);
      const stageKey = it.stage_key || 'default';
      for (const suffix of suffixes) {
        const v = readings[readingKey(it.stage_key, `${it.param_key}${suffix}`)];
        if (v == null || String(v).trim() === '') continue;
        const bucket = byStage.get(stageKey) || {};
        bucket[`${it.param_key}${suffix}`] = String(v).trim();
        byStage.set(stageKey, bucket);
      }
    }
    const stages = Array.from(byStage.entries()).map(([stage_key, vals]) => ({
      stage_key,
      stage_name: items.find((i) => (i.stage_key || 'default') === stage_key)?.stage_name || stage_key,
      readings: vals,
    }));
    return run(
      () => api.post(`/api/rework-batches/inspections/${encodeURIComponent(insId)}/readings`, {
        stages, ...(readingsResult ? { result: readingsResult } : {}),
      }),
      '实测值已记录',
    ).then(() => { setReadingsFor(null); setReadings({}); setReadingsItems([]); setReadingsResult(''); });
  };

  // 建立本次返工检验：原检验有模板 → 沿用继承快照（空 body）；无模板 → 必须带模板与确认原因，
  // 由后端用所选模板构建本次快照（随后录值/放行沿用现有逻辑）。
  //
  // 2026-10-23 业务口径（售后返工）：「调出原质检单 → 工程师改数据 → 保存到新的质检单」。
  // 原单只读保留；新单以原单实际阶段为基准（后端复制 stages），改过的值随 body.stages 提交。
  const openSourceForm = () => {
    const stages = ctx?.source_stages || [];
    const init: Record<string, Record<string, string>> = {};
    for (const st of stages) {
      const vals: Record<string, string> = {};
      for (const [k, v] of Object.entries(st.readings || {})) {
        vals[k] = v == null ? '' : String(v);
      }
      init[st.stage_key] = vals;
    }
    setSrcEdit(init);
    setSrcNote('');
    setSrcDate(new Date().toISOString().slice(0, 10));
    setSrcOpen(true);
  };

  // 把原单读数编辑区的内容组装成 body.stages（只提交改过或有值的项，空值不提交以免覆盖成空）。
  const submitSourceForm = async () => {
    setErr(''); setMsg('');
    const stages = (ctx?.source_stages || []).map((st) => {
      const edited = srcEdit[st.stage_key] || {};
      const readings: Record<string, string> = {};
      for (const [k, v] of Object.entries(edited)) {
        const val = String(v ?? '').trim();
        if (val !== '') readings[k] = val;
      }
      return {
        stage_key: st.stage_key,
        stage_name: st.stage_name || st.stage_key,
        stage_order: st.stage_order ?? 0,
        readings,
        notes: st.notes ?? null,
      };
    }).filter((s) => Object.keys(s.readings).length > 0);
    if (!stages.length) { setErr('请至少录入一项实测值'); return; }
    setBusy(true);
    try {
      const body: Record<string, unknown> = { stages };
      // 原单无模板时仍需带模板与确认原因（后端门禁）。
      if (templateConfirmationRequired) {
        if (!tplChoice.template_id) { setErr('请选择本次返工使用的质检模板'); setBusy(false); return; }
        if (!tplChoice.confirmed_reason.trim()) { setErr('请填写模板确认原因（必填）'); setBusy(false); return; }
        body.template_id = tplChoice.template_id;
        body.confirmed_reason = tplChoice.confirmed_reason.trim();
      }
      if (srcDate) body.inspection_date = srcDate;
      const out: any = await api.post(`${base}/inspections`, body);
      const d = out?.data ?? out;
      setMsg(`已按原质检单生成新的返工质检单 ${d?.number || ''}（${stages.length} 个阶段，${srcNote.trim() ? `说明：${srcNote.trim()}` : '原单只读保留'}）`);
      setSrcOpen(false); setSrcEdit({}); setSrcNote('');
      setTplChoice({ template_id: '', confirmed_reason: '' });
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || '保存返工质检单失败');
    } finally {
      setBusy(false);
    }
  };

  const createInspection = async () => {
    setErr(''); setMsg('');
    if (templateConfirmationRequired) {
      if (!tplChoice.template_id) { setErr('请选择本次返工使用的质检模板'); return; }
      if (!tplChoice.confirmed_reason.trim()) { setErr('请填写模板确认原因（必填）'); return; }
    }
    setBusy(true);
    try {
      const body = templateConfirmationRequired
        ? { template_id: tplChoice.template_id, confirmed_reason: tplChoice.confirmed_reason.trim() }
        : {};
      const out: any = await api.post(`${base}/inspections`, body);
      const d = out?.data ?? out;
      const createdItems: SnapshotItem[] = Array.isArray(d?.snapshot_items)
        ? d.snapshot_items
        : parseSnapshotItems(d?.snapshot_items_json);
      setMsg(templateConfirmationRequired
        ? `已确认模板 · 快照 ${createdItems.length} 项`
          + (createdItems.length ? '；可在「录值」按该快照录入实测值后放行。' : '；该模板未返回检查项，请检查模板配置。')
        : '检验已建立（快照已冻结）');
      setTplChoice({ template_id: '', confirmed_reason: '' });
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || '建立检验失败');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="正在读取工序与检验…" />;

  return (
    <div className="panel" style={{ margin: '6px 0', padding: 12, background: 'var(--surface-2, #f7f7f7)' }}>
      <div className="strong" style={{ marginBottom: 6 }}>返工执行 · {itemLabel}</div>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      {/* 工序 */}
      <table className="grid">
        <thead>
          <tr>
            <th>工序</th><th style={{ textAlign: 'right' }}>计划工时</th><th style={{ textAlign: 'right' }}>实际工时</th>
            <th style={{ textAlign: 'right' }}>熔覆(h)</th><th>状态</th><th>粉末耗用</th><th></th>
          </tr>
        </thead>
        <tbody>
          {ops.length === 0 ? <tr><td colSpan={7} className="muted">暂无工序</td></tr> : null}
          {ops.map((o) => (
            <tr key={o.id}>
              <td>{o.operation_name || o.operation_code || '—'}<span className="muted">{o.operation_code ? ` (${o.operation_code})` : ''}</span></td>
              <td style={{ textAlign: 'right' }}>{o.planned_hours ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{o.actual_hours ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{o.planned_clad_hours ?? '—'}</td>
              <td className="muted">{o.status || '—'}</td>
              <td className="muted">
                {(o.powder_usages || []).length === 0 ? '—' : (o.powder_usages || []).map((u) => (
                  <div key={u.id}>
                    {u.powder_name || u.item_name || '粉末'} {u.actual_quantity}{u.actual_unit || ''}
                    {u.adjust_of_id ? <span className="muted"> · 调整</span> : null}
                    {u.stock_movement_id ? <span className="muted"> · 已扣库</span> : null}
                    {canWrite && !u.adjust_of_id ? (
                      <button className="btn" style={{ marginLeft: 6 }} onClick={() => { setAdjustFor(u.id); setAdjustQty(String(u.actual_quantity ?? '')); }}>调整</button>
                    ) : null}
                  </div>
                ))}
              </td>
              <td>
                {canWrite ? (
                  <>
                    <button className="btn" onClick={() => setPowderFor(powderFor === o.id ? null : o.id)}>耗用</button>
                    <button
                      className="btn"
                      style={{ marginLeft: 4 }}
                      onClick={() => { setEditOpId(editOpId === o.id ? null : o.id); setEditOpHours(String(o.planned_hours ?? '')); }}
                    >编辑</button>
                    <button
                      className="btn"
                      style={{ marginLeft: 4 }}
                      onClick={() => run(() => api.del(`/api/rework-batches/operations/${encodeURIComponent(o.id)}`), '工序已删除')}
                    >删除</button>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editOpId ? (
        <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ width: 140 }}>
            <TextInput type="number" min={0} step="0.1" placeholder="计划工时（h）" value={editOpHours} onChange={(e) => setEditOpHours(e.target.value)} />
          </div>
          <button
            className="btn primary"
            disabled={busy || editOpHours.trim() === '' || !Number.isFinite(Number(editOpHours))}
            onClick={() => run(
              () => api.patch(`/api/rework-batches/operations/${encodeURIComponent(editOpId)}`, { planned_hours: Number(editOpHours) }),
              '工序工时已更新',
            ).then(() => setEditOpId(null))}
          >保存工时</button>
          <button className="btn" onClick={() => setEditOpId(null)}>取消</button>
        </div>
      ) : null}

      {powderFor ? (
        <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <div style={{ width: 150 }}>
            <SelectInput value={powder.issue_mode} onChange={(e) => setPowder((s) => ({ ...s, issue_mode: e.target.value as 'immediate' | 'work_order_link', snapshot_id: '' }))}>
              <option value="immediate">即时扣库</option>
              <option value="work_order_link">关联工单领料核销</option>
            </SelectInput>
          </div>
          <div style={{ width: 220 }}>
            <SearchSelect<ItemRow>
              endpoint="/api/items"
              value={powder.item_id || null}
              label={powder.item_label}
              optionTitle={(r) => r.name_zh || r.code || r.id}
              optionMeta={(r) => r.code}
              placeholder="选择粉末物料…"
              onChange={(r) => { const id = r?.id || ''; setPowder((s) => ({ ...s, item_id: id, item_label: r?.name_zh || r?.code || '', snapshot_id: '' })); loadPowderCandidates(id); }}
            />
          </div>
          {powder.issue_mode === 'work_order_link' ? (
            <div style={{ width: 260 }}>
              <SelectInput value={powder.snapshot_id} onChange={(e) => setPowder((s) => ({ ...s, snapshot_id: e.target.value }))}>
                <option value="">选择工单领料（核销剩余）…</option>
                {powderCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.work_order_number || '工单'} · 剩余 {c.remaining_base ?? 0}
                  </option>
                ))}
              </SelectInput>
            </div>
          ) : null}
          <div style={{ width: 110 }}>
            <TextInput type="number" min={0} placeholder="用量" value={powder.actual_quantity} onChange={(e) => setPowder((s) => ({ ...s, actual_quantity: e.target.value }))} />
          </div>
          <div style={{ width: 80 }}>
            <SelectInput value={powder.actual_unit} onChange={(e) => setPowder((s) => ({ ...s, actual_unit: e.target.value }))}>
              <option value="kg">kg</option><option value="g">g</option>
            </SelectInput>
          </div>
          <button
            className="btn primary"
            disabled={busy || !powder.item_id || !powder.actual_quantity || (powder.issue_mode === 'work_order_link' && !powder.snapshot_id)}
            onClick={() => addPowder(powderFor)}
          >{powder.issue_mode === 'work_order_link' ? '核销/补扣登记' : '登记并扣库'}</button>
          <button className="btn" onClick={resetPowder}>取消</button>
        </div>
      ) : null}

      {/* 统一工序编辑器：机加工四选（各自工时）/ 激光熔覆（粉末+耗料+工时） */}
      <OperationEditor
        endpoint={base}
        canWrite={canWrite}
        onChanged={() => { load(); onChanged(); }}
      />

      {adjustFor ? (
        <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ width: 110 }}><TextInput type="number" min={0} placeholder="调整后用量" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)} /></div>
          <div style={{ width: 220 }}><TextInput placeholder="调整原因" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} /></div>
          <button
            className="btn primary"
            disabled={busy || !adjustQty}
            onClick={() => run(() => api.post(`/api/rework-batches/powder/${encodeURIComponent(adjustFor)}/adjust`, {
              actual_quantity: Number(adjustQty), adjustment_reason: adjustReason.trim() || undefined,
            }), '耗用已调整').then(() => { setAdjustFor(null); setAdjustReason(''); setAdjustQty(''); })}
          >确认调整</button>
          <button className="btn" onClick={() => setAdjustFor(null)}>取消</button>
        </div>
      ) : null}

      {/* 本次返工检验 */}
      <div style={{ marginTop: 10 }}>
        <div className="strong">本次返工检验（模板快照继承）</div>
        <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
          原检验 {ctx?.original_inspection?.number || '—'}（结论 {ctx?.original_inspection?.result || '—'}）
          · {templateConfirmationRequired
            ? '原检验无模板 · 需质检确认本次模板'
            : `快照${ctx?.snapshot_available ? `可复原（${ctx.snapshot_items.length} 项）` : '不可用'}`}
          · 原单只读保留
        </div>

        {/* 调出原质检单：以原单实际阶段为初值，改完存成新的返工质检单（原单不动）。 */}
        {canWrite && (ctx?.source_stages?.length || 0) > 0 ? (
          <div style={{ margin: '6px 0' }}>
            {!srcOpen ? (
              <button className="btn" disabled={busy} onClick={openSourceForm}>
                调出原质检单改数据（{ctx?.source_inspection_stage_count || ctx?.source_stages?.length || 0} 个阶段）
              </button>
            ) : (
              <div className="panel" style={{ padding: 10, background: 'var(--surface-2, #f7f7f7)' }}>
                <div className="strong" style={{ marginBottom: 4 }}>
                  原质检单 {ctx?.original_inspection?.number || ''} · 修改后保存为新的返工质检单
                </div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  下面是原单的实际读数，直接改数值即可；保存会生成一张新的返工质检单，原单原样保留。
                </div>
                {(ctx?.source_stages || []).map((st) => (
                  <div key={st.stage_key} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{st.stage_name || st.stage_key}</div>
                    <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6 }}>
                      {Object.entries(srcEdit[st.stage_key] || {}).map(([param, val]) => (
                        <label key={param} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                          <span className="muted">{param}</span>
                          <div style={{ width: 96 }}>
                            <TextInput
                              value={val}
                              onChange={(e) => setSrcEdit((s) => ({
                                ...s, [st.stage_key]: { ...(s[st.stage_key] || {}), [param]: e.target.value },
                              }))}
                              aria-label={`${st.stage_key}-${param}`}
                            />
                          </div>
                        </label>
                      ))}
                      {Object.keys(srcEdit[st.stage_key] || {}).length === 0
                        ? <span className="muted" style={{ fontSize: 12 }}>（原单该阶段无读数）</span> : null}
                    </div>
                  </div>
                ))}
                <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  <div style={{ width: 150 }}>
                    <TextInput type="date" value={srcDate} onChange={(e) => setSrcDate(e.target.value)} aria-label="检验日期" />
                  </div>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <TextInput
                      placeholder="修改说明（选填，例如：返工后最终尺寸复测）"
                      value={srcNote}
                      onChange={(e) => setSrcNote(e.target.value)}
                      aria-label="修改说明"
                    />
                  </div>
                </div>
                <div className="detail-actions" style={{ gap: 6, marginTop: 6 }}>
                  <button className="btn primary" disabled={busy} onClick={submitSourceForm}>保存为新的返工质检单</button>
                  <button className="btn" disabled={busy} onClick={() => { setSrcOpen(false); setSrcEdit({}); setSrcNote(''); }}>取消</button>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {canWrite && templateConfirmationRequired ? (
          <div className="panel" style={{ margin: '6px 0', padding: 10, background: 'var(--surface-2, #f7f7f7)' }}>
            <div className="strong" style={{ marginBottom: 4 }}>质检确认本次返工模板（原检验无可继承模板）</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              原检验无模板或快照不可用：需质检人员确认本次使用的模板并记录原因；确认后按该模板构建快照，原检验原样保留。
            </div>
            <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'flex-start' }}>
              <div style={{ width: 300 }}>
                <SelectInput
                  value={tplChoice.template_id}
                  onChange={(e) => setTplChoice((s) => ({ ...s, template_id: e.target.value }))}
                  aria-label="选择本次返工质检模板"
                >
                  <option value="">选择质检模板…</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} · {t.name_zh}{Number(t.active ?? 1) ? '' : '（停用）'}
                    </option>
                  ))}
                </SelectInput>
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <TextArea
                  placeholder="模板确认原因（必填，例如：原检验未上传模板 / 原模板缺失，按现行检验规范补录）"
                  value={tplChoice.confirmed_reason}
                  onChange={(e) => setTplChoice((s) => ({ ...s, confirmed_reason: e.target.value }))}
                  aria-label="模板确认原因"
                />
              </div>
            </div>
          </div>
        ) : null}

        <table className="grid">
          <thead><tr><th>检验单号</th><th>轮次</th><th>实测数据</th><th>放行状态</th><th>检验员</th><th></th></tr></thead>
          <tbody>
            {insps.length === 0 ? <tr><td colSpan={6} className="muted">暂无本次返工检验</td></tr> : null}
            {insps.map((it) => {
              const r = RELEASE[it.release_state] || RELEASE.draft;
              // 质检只记录数据、不判定合格/不合格：这里不再显示 accepted/pending 之类的「结论」，
              // 改为「已录值 / 待录值」（放行状态单独一列）。
              //
              // 2026-10-26 生产反馈：误点「建立本次返工检验」后直接显示「已录值」，导致删不掉。
              // 根因：建单会把原单阶段复制过来当初值，stage_count>0 但工程师并未录值。
              // 因此改为看**真实有值的读数项数**（后端 has_readings / reading_value_count）。
              const valueCount = Number(it.reading_value_count ?? 0);
              const hasReadings = it.has_readings != null
                ? !!it.has_readings
                : valueCount > 0 || Number(it.stage_count ?? 0) > 0;
              return (
                <tr key={it.id}>
                  <td>{it.number}</td>
                  <td className="muted">{it.inspection_round ?? '—'}</td>
                  <td className="muted">
                    {hasReadings ? '已录值' : '待录值'}
                    {hasReadings && valueCount ? <span className="muted">（{valueCount} 项）</span> : null}
                  </td>
                  <td><Pill tone={r.tone} label={r.label} /></td>
                  <td className="muted">{it.inspector_name || '—'}{it.invalidated_reason ? ` · ${it.invalidated_reason}` : ''}</td>
                  <td>
                    {canWrite && it.release_state === 'draft' ? (
                      <>
                        <button className="btn" disabled={busy} onClick={() => toggleReadings(it)}>录值</button>
                        <button className="btn primary" style={{ marginLeft: 4 }} disabled={busy} onClick={() => run(() => api.post(`/api/rework-batches/inspections/${encodeURIComponent(it.id)}/release`, {}), '检验已放行')}>放行</button>
                        {/* 删除草稿单（2026-10-26 生产反馈：误点建立、或录错值都要能删）。
                            口径：draft 一律可删（有读数也行，尚未放行、不构成对外报告）；
                            已放行的单不显示删除，须走「失效」保留追溯。 */}
                        <button
                          className="btn"
                          style={{ marginLeft: 4 }}
                          disabled={busy}
                          title="删除这张草稿检验单（已放行的须用「失效」）"
                          onClick={() => {
                            const extra = hasReadings
                              ? `\n\n注意：该单已录入 ${valueCount} 项实测值，删除后这些读数一并丢失。`
                              : '';
                            if (!confirm(`删除 ${it.number}？${extra}\n\n删除后不可恢复；如需保留记录请改用「失效」。`)) return;
                            void run(
                              () => api.del(`/api/rework-batches/inspections/${encodeURIComponent(it.id)}`),
                              '检验单已删除',
                            );
                          }}
                        >删除</button>
                      </>
                    ) : null}
                    {canWrite && it.release_state === 'released' ? (
                      <>
                        <input className="txt" style={{ width: 130 }} placeholder="失效原因" value={invReason[it.id] || ''} onChange={(e) => setInvReason((s) => ({ ...s, [it.id]: e.target.value }))} />
                        <button className="btn" style={{ marginLeft: 4 }} disabled={busy || !(invReason[it.id] || '').trim()} onClick={() => run(() => api.post(`/api/rework-batches/inspections/${encodeURIComponent(it.id)}/invalidate`, { reason: invReason[it.id].trim() }), '检验已失效')}>失效</button>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {readingsFor ? (
          <div className="panel" style={{ marginTop: 6, padding: 10, background: 'var(--surface-2, #f7f7f7)' }}>
            <div className="strong" style={{ marginBottom: 4 }}>
              按本次检验快照录入实测值（{readingsItems.length} 项 · 记录后据实放行）
            </div>
            {readingsItems.length === 0 ? (
              <div className="notice">
                该检验快照无检查项：若原检验无模板，请先在上方确认质检模板并「建立本次返工检验」；随后在此录入实测值。
              </div>
            ) : null}
            <table className="grid">
              <thead><tr><th>工段</th><th>检验项</th><th>单位</th><th>必检</th><th>实测值</th></tr></thead>
              <tbody>
                {readingsItems.map((si) => {
                  // 模板声明的测量点数：平面度等需测多点（LECTURA 1/2/3）。
                  // 2026-10-26 生产反馈：此前恒为 1 个输入框，导致平面度只能录一个值、
                  // 出单也只有一格。这里按 points 渲染多列，键名用 `参数_1..N`（后端已支持）。
                  const pts = Math.max(1, Math.min(6, Number(si.points) || 1));
                  const cells = Array.from({ length: pts }, (_, i) => (pts === 1 ? si.param_key : `${si.param_key}_${i + 1}`));
                  const rowKey = `${si.stage_key || 'default'}::${si.param_key}`;
                  return (
                    <tr key={rowKey}>
                      <td className="muted">{si.stage_name || si.stage_key || '—'}</td>
                      <td>
                        {si.param_label_zh || si.param_key}
                        {pts > 1 ? <span className="muted" style={{ marginLeft: 6 }}>（{pts} 点）</span> : null}
                      </td>
                      <td className="muted">{si.unit || '—'}</td>
                      <td className="muted">{si.required ? '是' : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          {cells.map((cellKey, i) => (
                            <div key={cellKey} style={{ width: pts > 1 ? 96 : 160 }}>
                              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
                                {pts > 1 ? `LECTURA ${i + 1}` : ''}
                              </div>
                              <TextInput
                                value={readings[readingKey(si.stage_key, cellKey)] ?? ''}
                                placeholder={pts > 1 ? `第 ${i + 1} 点` : '实测值'}
                                aria-label={`${si.param_key}-${i + 1}`}
                                onChange={(e) => setReadings((s) => ({ ...s, [readingKey(si.stage_key, cellKey)]: e.target.value }))}
                              />
                            </div>
                          ))}
                          <button
                            className="btn"
                            style={{ height: 30, padding: '0 8px' }}
                            disabled={busy}
                            title="从本次录入中移除该项"
                            onClick={() => removeReadingItem(si)}
                          >移除</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {readingsItems.length === 0 ? <tr><td colSpan={5} className="muted">无可录入项</td></tr> : null}
              </tbody>
            </table>
            {/* 现场按需追加/移除检测项（2026-10-26 生产反馈）：
                返修时临时增加「抛光」工序才修得好，但原质检单里没有抛光阶段，
                录值表单也就没有抛光平面度。允许工程师自行从模板追加该工段。 */}
            <div className="detail-actions" style={{ marginTop: 8, flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 12 }}>现场追加检测项（本次新增了工序才需要）：</span>
              <div style={{ width: 260 }}>
                <SelectInput
                  value={addStageKey}
                  onChange={(e) => setAddStageKey(e.target.value)}
                  aria-label="选择要追加的工段"
                >
                  <option value="">选择工段…</option>
                  {addStageOptions.map((o) => (
                    <option key={o.stage_key} value={o.stage_key}>
                      {o.stage_name}（{o.params.join('、')}）
                    </option>
                  ))}
                </SelectInput>
              </div>
              <button className="btn" disabled={busy || !addStageKey} onClick={addStageItems}>追加到本表</button>
              {addStageOptions.length === 0 ? (
                <span className="muted" style={{ fontSize: 12 }}>（该模板的工段都已在表中）</span>
              ) : null}
            </div>
            <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
              {/* 业务口径（2026-10-23 用户明确）：售后返修同样不需要结论，客户拿报告即可。
                  因此不再提供「合格/不合格」选择，保存实测值后直接放行。 */}
              <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
                质检只记录实测数据，不判定合格/不合格；保存后即可放行出具报告
              </span>
              <button
                className="btn primary"
                disabled={busy || !Object.values(readings).some((v) => String(v).trim() !== '')}
                onClick={() => submitReadings(readingsFor)}
              >保存实测值</button>
              <button className="btn" onClick={() => { setReadingsFor(null); setReadings({}); setReadingsItems([]); setReadingsResult(''); }}>取消</button>
            </div>
          </div>
        ) : null}
        {canWrite ? (
          <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <button
              className="btn primary"
              disabled={busy || (templateConfirmationRequired && (!tplChoice.template_id || !tplChoice.confirmed_reason.trim()))}
              onClick={() => void createInspection()}
            >建立本次返工检验</button>
            {templateConfirmationRequired ? (
              <span className="muted" style={{ fontSize: 12 }}>
                {!tplChoice.template_id
                  ? '需先选择质检模板'
                  : !tplChoice.confirmed_reason.trim() ? '需填写模板确认原因' : '将按所选模板构建本次快照'}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function BatchApplyOperationsForm({ batchId, canWrite, onChanged }: { batchId: string; canWrite: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'per_item' | 'batch_total'>('per_item');
  const [form, setForm] = useState({ operation_code: '', operation_name: '', planned_hours: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  if (!canWrite) return null;

  const apply = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.post(`/api/rework-batches/${encodeURIComponent(batchId)}/operations/apply`, {
        mode,
        operations: [{
          operation_code: form.operation_code.trim() || undefined,
          operation_name: form.operation_name.trim() || undefined,
          planned_hours: form.planned_hours ? Number(form.planned_hours) : undefined,
        }],
      });
      setMsg(mode === 'batch_total' ? '已按整批工时均摊套用' : '已按每件套用');
      setForm({ operation_code: '', operation_name: '', planned_hours: '' });
      onChanged();
    } catch (e: any) { setErr(e?.message || '套用失败'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      {!open ? <button className="btn" onClick={() => setOpen(true)}>批量套用工序…</button> : (
        <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6 }}>
          <Field label="套用方式">
            <SelectInput value={mode} onChange={(e) => setMode(e.target.value as 'per_item' | 'batch_total')}>
              <option value="per_item">每件各套（per_item）</option>
              <option value="batch_total">整批工时均摊（batch_total）</option>
            </SelectInput>
          </Field>
          <div style={{ width: 150 }}><TextInput placeholder="工序编码" value={form.operation_code} onChange={(e) => setForm((s) => ({ ...s, operation_code: e.target.value }))} /></div>
          <div style={{ width: 150 }}><TextInput placeholder="工序名称" value={form.operation_name} onChange={(e) => setForm((s) => ({ ...s, operation_name: e.target.value }))} /></div>
          <div style={{ width: 120 }}><TextInput type="number" min={0} placeholder="计划工时" value={form.planned_hours} onChange={(e) => setForm((s) => ({ ...s, planned_hours: e.target.value }))} /></div>
          <button className="btn primary" disabled={busy || (!form.operation_code && !form.operation_name)} onClick={apply}>套用</button>
          <button className="btn" onClick={() => setOpen(false)}>关闭</button>
          {err ? <span className="notice bad">{err}</span> : null}
          {msg ? <span className="notice">{msg}</span> : null}
        </div>
      )}
    </div>
  );
}
